import type { Locale } from './types';

const EN = {
  mySeries: 'My series', seriesNotifications: 'Notifications', markAllSeriesRead: 'Mark all as read',
  moreSeries: 'More series', moreMySeries: 'More of my series', moreSeriesNotices: 'More notifications',
  orderSeason: 'Order season', orderSeriesEpisode: 'Order episode',
  seriesOrderConfirmTitle: 'Add this season to your library?',
  seriesOrderConfirmBody: 'We will find and prepare season {season} of {title}. It will appear here when ready.',
  seriesWatchedPercent: '{count}% watched', episodeProgressFailed: 'Could not save episode progress',
  followSeries: 'Follow', unfollowSeries: 'Unfollow', notifySeriesReady: 'Notify when a season is ready',
  notifyNewSeriesSeason: 'Notify about new seasons', autoOrderSeriesSeasons: 'Order new seasons automatically',
  seriesFinale: 'Finale', seriesAirsOn: 'Airs {date}', seriesNextSearch: 'Next search {date}',
  seriesNoSource: 'No source found', seriesSourceFailed: 'Source failed', seriesRejected: 'Source rejected',
  seriesUpcoming: 'Upcoming', seriesCollected: 'Collected, waiting for season',
  seriesSection: "Series",
  seriesSeasonReadyNotice: 'Series "{title}", season {season}, has been added to the library and is ready to watch',
  seriesUnread: "{count} new",
  recentlyAddedSeries: "Recently added",
  allSeries: "← All series",
  seriesEpisodesReady: "{count} episodes",
  minutesShort: "{count} min",
  minutesWatched: "{count} min watched",
  writers: "Writing",
  seriesLoadFailed: "Could not load series",
  seriesOpenFailed: "Could not open the series",
  seasonLoadFailed: "Could not load the season",
  episodeLoadFailed: "Could not load the episode",
  episodeNotPlayable: "This episode is not available yet",
  seasonOrderFailed: "Could not order the season",
  episodeOrderFailed: "Could not order the episode",
  followUpdateFailed: "Could not update the subscription",
  noticeOpenFailed: "Could not open the notification",
  nextNotAired: "The next episode has not aired yet",
  nextCheckFailed: "Could not check the next episode",
  seriesStatusReady: "Ready",
  seriesStatusProcessing: "Preparing",
  seriesStatusDownloading: "Downloading",
  seriesStatusQueued: "In queue",
  seriesStatusSearching: "Looking for a source",
  seriesStatusUnavailable: "Unavailable",
  seriesStatusNotRequested: "Not ordered",
  seriesEpisodeOrdered: 'Episode ordered. It will appear here when ready.', previousEpisode: 'Previous episode',
} as const;
type Key = keyof typeof EN;
const RU: Record<Key, string> = {
  mySeries: 'Мои сериалы', seriesNotifications: 'Уведомления', markAllSeriesRead: 'Прочитать все',
  moreSeries: 'Ещё сериалы', moreMySeries: 'Ещё мои сериалы', moreSeriesNotices: 'Ещё уведомления',
  orderSeason: 'Заказать сезон', orderSeriesEpisode: 'Заказать серию',
  seriesOrderConfirmTitle: 'Добавить сезон в библиотеку?',
  seriesOrderConfirmBody: 'Найдём и подготовим сезон {season} сериала «{title}». Когда он будет готов, он появится здесь.',
  seriesWatchedPercent: 'Просмотрено {count}%', episodeProgressFailed: 'Не удалось сохранить прогресс серии',
  followSeries: 'Подписаться', unfollowSeries: 'Отписаться', notifySeriesReady: 'Уведомлять о готовом сезоне',
  notifyNewSeriesSeason: 'Уведомлять о новых сезонах', autoOrderSeriesSeasons: 'Автоматически заказывать новые сезоны',
  seriesFinale: 'Финал', seriesAirsOn: 'Выйдет {date}', seriesNextSearch: 'Следующая попытка {date}',
  seriesNoSource: 'Источник не найден', seriesSourceFailed: 'Источник не сработал', seriesRejected: 'Источник отклонён',
  seriesUpcoming: 'Ещё не вышла', seriesCollected: 'Собрана, ждём сезон',
  seriesSection: "Сериалы",
  seriesSeasonReadyNotice: 'Сериал «{title}», сезон {season}, уже добавлен в библиотеку и готов к просмотру',
  seriesUnread: "новых: {count}",
  recentlyAddedSeries: "Недавно добавлены",
  allSeries: "← Все сериалы",
  seriesEpisodesReady: "Серий: {count}",
  minutesShort: "{count} мин",
  minutesWatched: "Просмотрено {count} мин",
  writers: "Сценарий",
  seriesLoadFailed: "Не удалось загрузить сериалы",
  seriesOpenFailed: "Не удалось открыть сериал",
  seasonLoadFailed: "Не удалось загрузить сезон",
  episodeLoadFailed: "Не удалось загрузить серию",
  episodeNotPlayable: "Серия пока недоступна",
  seasonOrderFailed: "Не удалось заказать сезон",
  episodeOrderFailed: "Не удалось заказать серию",
  followUpdateFailed: "Не удалось обновить подписку",
  noticeOpenFailed: "Не удалось открыть уведомление",
  nextNotAired: "Следующая серия ещё не вышла",
  nextCheckFailed: "Не удалось проверить следующую серию",
  seriesStatusReady: "Готова",
  seriesStatusProcessing: "Обработка",
  seriesStatusDownloading: "Качается",
  seriesStatusQueued: "В очереди",
  seriesStatusSearching: "Ищем источник",
  seriesStatusUnavailable: "Недоступна",
  seriesStatusNotRequested: "Не заказана",
  seriesEpisodeOrdered: 'Серия заказана. Она появится здесь, когда будет готова.', previousEpisode: 'Предыдущая серия',
};
const DE: Record<Key, string> = {
  seriesOrderConfirmTitle: 'Staffel zur Bibliothek hinzufügen?',
  seriesOrderConfirmBody: 'Wir suchen und bereiten Staffel {season} von {title} vor. Sie erscheint hier, sobald sie fertig ist.',
  seriesWatchedPercent: '{count}% angesehen',
  episodeProgressFailed: 'Der Wiedergabefortschritt konnte nicht gespeichert werden',
  mySeries:'Meine Serien',seriesNotifications:'Benachrichtigungen',markAllSeriesRead:'Alle als gelesen markieren',moreSeries:'Weitere Serien',moreMySeries:'Weitere meiner Serien',moreSeriesNotices:'Weitere Benachrichtigungen',orderSeason:'Staffel bestellen',orderSeriesEpisode:'Folge bestellen',followSeries:'Folgen',unfollowSeries:'Entfolgen',notifySeriesReady:'Über fertige Staffeln informieren',notifyNewSeriesSeason:'Über neue Staffeln informieren',autoOrderSeriesSeasons:'Neue Staffeln automatisch bestellen',seriesFinale:'Finale',seriesAirsOn:'Erscheint {date}',seriesNextSearch:'Nächste Suche {date}',seriesNoSource:'Keine Quelle gefunden',seriesSourceFailed:'Quelle fehlgeschlagen',seriesRejected:'Quelle abgelehnt',seriesUpcoming:'Noch nicht erschienen',seriesCollected:'Gesammelt, Staffel noch unvollständig',
  seriesSection: "Serien",
  seriesSeasonReadyNotice: 'Serie „{title}“, Staffel {season}, ist in der Bibliothek und bereit zum Ansehen',
  seriesUnread: "{count} neu",
  recentlyAddedSeries: "Neu hinzugefügt",
  allSeries: "← Alle Serien",
  seriesEpisodesReady: "{count} Folgen",
  minutesShort: "{count} Min.",
  minutesWatched: "{count} Min. gesehen",
  writers: "Drehbuch",
  seriesLoadFailed: "Serien konnten nicht geladen werden",
  seriesOpenFailed: "Serie konnte nicht geöffnet werden",
  seasonLoadFailed: "Staffel konnte nicht geladen werden",
  episodeLoadFailed: "Folge konnte nicht geladen werden",
  episodeNotPlayable: "Diese Folge ist noch nicht verfügbar",
  seasonOrderFailed: "Staffel konnte nicht bestellt werden",
  episodeOrderFailed: "Folge konnte nicht bestellt werden",
  followUpdateFailed: "Abo konnte nicht aktualisiert werden",
  noticeOpenFailed: "Benachrichtigung konnte nicht geöffnet werden",
  nextNotAired: "Die nächste Folge ist noch nicht erschienen",
  nextCheckFailed: "Nächste Folge konnte nicht geprüft werden",
  seriesStatusReady: "Bereit",
  seriesStatusProcessing: "Wird vorbereitet",
  seriesStatusDownloading: "Wird geladen",
  seriesStatusQueued: "In der Warteschlange",
  seriesStatusSearching: "Quelle wird gesucht",
  seriesStatusUnavailable: "Nicht verfügbar",
  seriesStatusNotRequested: "Nicht bestellt",
  seriesEpisodeOrdered: 'Folge bestellt. Sie erscheint hier, sobald sie bereit ist.', previousEpisode: 'Vorherige Folge',
};
const FR: Record<Key, string> = {
  seriesOrderConfirmTitle: 'Ajouter cette saison à la bibliothèque ?',
  seriesOrderConfirmBody: 'Nous chercherons et préparerons la saison {season} de {title}. Elle apparaîtra ici une fois prête.',
  seriesWatchedPercent: '{count}% regardé',
  episodeProgressFailed: 'Impossible d’enregistrer la progression',
  mySeries:'Mes séries',seriesNotifications:'Notifications',markAllSeriesRead:'Tout marquer comme lu',moreSeries:'Plus de séries',moreMySeries:'Plus de mes séries',moreSeriesNotices:'Plus de notifications',orderSeason:'Commander la saison',orderSeriesEpisode:'Commander l’épisode',followSeries:'Suivre',unfollowSeries:'Ne plus suivre',notifySeriesReady:'Notifier quand une saison est prête',notifyNewSeriesSeason:'Notifier les nouvelles saisons',autoOrderSeriesSeasons:'Commander automatiquement les nouvelles saisons',seriesFinale:'Finale',seriesAirsOn:'Sortie le {date}',seriesNextSearch:'Prochaine recherche {date}',seriesNoSource:'Aucune source trouvée',seriesSourceFailed:'Source indisponible',seriesRejected:'Source rejetée',seriesUpcoming:'Pas encore diffusé',seriesCollected:'Collecté, saison en attente',
  seriesSection: "Séries",
  seriesSeasonReadyNotice: 'La série « {title} », saison {season}, a été ajoutée à la bibliothèque et est prête à regarder',
  seriesUnread: "{count} nouvelles",
  recentlyAddedSeries: "Ajoutées récemment",
  allSeries: "← Toutes les séries",
  seriesEpisodesReady: "{count} épisodes",
  minutesShort: "{count} min",
  minutesWatched: "{count} min vues",
  writers: "Scénario",
  seriesLoadFailed: "Impossible de charger les séries",
  seriesOpenFailed: "Impossible d’ouvrir la série",
  seasonLoadFailed: "Impossible de charger la saison",
  episodeLoadFailed: "Impossible de charger l’épisode",
  episodeNotPlayable: "Cet épisode n’est pas encore disponible",
  seasonOrderFailed: "Impossible de commander la saison",
  episodeOrderFailed: "Impossible de commander l’épisode",
  followUpdateFailed: "Impossible de mettre à jour l’abonnement",
  noticeOpenFailed: "Impossible d’ouvrir la notification",
  nextNotAired: "Le prochain épisode n’est pas encore diffusé",
  nextCheckFailed: "Impossible de vérifier le prochain épisode",
  seriesStatusReady: "Prêt",
  seriesStatusProcessing: "Préparation",
  seriesStatusDownloading: "Téléchargement",
  seriesStatusQueued: "En file d’attente",
  seriesStatusSearching: "Recherche d’une source",
  seriesStatusUnavailable: "Indisponible",
  seriesStatusNotRequested: "Non commandé",
  seriesEpisodeOrdered: 'Épisode commandé. Il apparaîtra ici dès qu’il sera prêt.', previousEpisode: 'Épisode précédent',
};
const IT: Record<Key, string> = {
  seriesOrderConfirmTitle: 'Aggiungere questa stagione alla libreria?',
  seriesOrderConfirmBody: 'Cercheremo e prepareremo la stagione {season} di {title}. Apparirà qui quando sarà pronta.',
  seriesWatchedPercent: '{count}% guardato',
  episodeProgressFailed: 'Impossibile salvare i progressi dell’episodio',
  mySeries:'Le mie serie',seriesNotifications:'Notifiche',markAllSeriesRead:'Segna tutto come letto',moreSeries:'Altre serie',moreMySeries:'Altre mie serie',moreSeriesNotices:'Altre notifiche',orderSeason:'Richiedi stagione',orderSeriesEpisode:'Richiedi episodio',followSeries:'Segui',unfollowSeries:'Non seguire',notifySeriesReady:'Avvisa quando una stagione è pronta',notifyNewSeriesSeason:'Avvisa delle nuove stagioni',autoOrderSeriesSeasons:'Richiedi automaticamente nuove stagioni',seriesFinale:'Finale',seriesAirsOn:'Esce il {date}',seriesNextSearch:'Prossima ricerca {date}',seriesNoSource:'Nessuna fonte trovata',seriesSourceFailed:'Fonte non disponibile',seriesRejected:'Fonte rifiutata',seriesUpcoming:'Non ancora uscito',seriesCollected:'Raccolto, stagione in attesa',
  seriesSection: "Serie",
  seriesSeasonReadyNotice: 'La serie «{title}», stagione {season}, è stata aggiunta alla libreria ed è pronta da guardare',
  seriesUnread: "{count} nuove",
  recentlyAddedSeries: "Aggiunte di recente",
  allSeries: "← Tutte le serie",
  seriesEpisodesReady: "{count} episodi",
  minutesShort: "{count} min",
  minutesWatched: "{count} min visti",
  writers: "Sceneggiatura",
  seriesLoadFailed: "Impossibile caricare le serie",
  seriesOpenFailed: "Impossibile aprire la serie",
  seasonLoadFailed: "Impossibile caricare la stagione",
  episodeLoadFailed: "Impossibile caricare l’episodio",
  episodeNotPlayable: "Episodio non ancora disponibile",
  seasonOrderFailed: "Impossibile richiedere la stagione",
  episodeOrderFailed: "Impossibile richiedere l’episodio",
  followUpdateFailed: "Impossibile aggiornare l’iscrizione",
  noticeOpenFailed: "Impossibile aprire la notifica",
  nextNotAired: "Il prossimo episodio non è ancora uscito",
  nextCheckFailed: "Impossibile verificare il prossimo episodio",
  seriesStatusReady: "Pronto",
  seriesStatusProcessing: "In preparazione",
  seriesStatusDownloading: "Download in corso",
  seriesStatusQueued: "In coda",
  seriesStatusSearching: "Ricerca fonte",
  seriesStatusUnavailable: "Non disponibile",
  seriesStatusNotRequested: "Non richiesto",
  seriesEpisodeOrdered: 'Episodio ordinato. Apparirà qui quando sarà pronto.', previousEpisode: 'Episodio precedente',
};
const ES: Record<Key, string> = {
  seriesOrderConfirmTitle: '¿Añadir esta temporada a la biblioteca?',
  seriesOrderConfirmBody: 'Buscaremos y prepararemos la temporada {season} de {title}. Aparecerá aquí cuando esté lista.',
  seriesWatchedPercent: '{count}% visto',
  episodeProgressFailed: 'No se pudo guardar el progreso del episodio',
  mySeries:'Mis series',seriesNotifications:'Notificaciones',markAllSeriesRead:'Marcar todo como leído',moreSeries:'Más series',moreMySeries:'Más de mis series',moreSeriesNotices:'Más notificaciones',orderSeason:'Pedir temporada',orderSeriesEpisode:'Pedir episodio',followSeries:'Seguir',unfollowSeries:'Dejar de seguir',notifySeriesReady:'Avisar cuando una temporada esté lista',notifyNewSeriesSeason:'Avisar de nuevas temporadas',autoOrderSeriesSeasons:'Pedir nuevas temporadas automáticamente',seriesFinale:'Final',seriesAirsOn:'Sale el {date}',seriesNextSearch:'Próxima búsqueda {date}',seriesNoSource:'No se encontró fuente',seriesSourceFailed:'Falló la fuente',seriesRejected:'Fuente rechazada',seriesUpcoming:'Aún no emitido',seriesCollected:'Recopilado, esperando temporada',
  seriesSection: "Series",
  seriesSeasonReadyNotice: 'La serie «{title}», temporada {season}, se añadió a la biblioteca y está lista para ver',
  seriesUnread: "{count} nuevas",
  recentlyAddedSeries: "Añadidas recientemente",
  allSeries: "← Todas las series",
  seriesEpisodesReady: "{count} episodios",
  minutesShort: "{count} min",
  minutesWatched: "{count} min vistos",
  writers: "Guion",
  seriesLoadFailed: "No se pudieron cargar las series",
  seriesOpenFailed: "No se pudo abrir la serie",
  seasonLoadFailed: "No se pudo cargar la temporada",
  episodeLoadFailed: "No se pudo cargar el episodio",
  episodeNotPlayable: "Este episodio aún no está disponible",
  seasonOrderFailed: "No se pudo pedir la temporada",
  episodeOrderFailed: "No se pudo pedir el episodio",
  followUpdateFailed: "No se pudo actualizar la suscripción",
  noticeOpenFailed: "No se pudo abrir la notificación",
  nextNotAired: "El siguiente episodio aún no se ha emitido",
  nextCheckFailed: "No se pudo comprobar el siguiente episodio",
  seriesStatusReady: "Listo",
  seriesStatusProcessing: "Preparando",
  seriesStatusDownloading: "Descargando",
  seriesStatusQueued: "En cola",
  seriesStatusSearching: "Buscando fuente",
  seriesStatusUnavailable: "No disponible",
  seriesStatusNotRequested: "No pedido",
  seriesEpisodeOrdered: 'Episodio pedido. Aparecerá aquí cuando esté listo.', previousEpisode: 'Episodio anterior',
};
const PL: Record<Key, string> = {
  seriesOrderConfirmTitle: 'Dodać ten sezon do biblioteki?',
  seriesOrderConfirmBody: 'Znajdziemy i przygotujemy sezon {season} serialu {title}. Pojawi się tutaj, gdy będzie gotowy.',
  seriesWatchedPercent: 'Obejrzano {count}%',
  episodeProgressFailed: 'Nie udało się zapisać postępu odcinka',
  mySeries:'Moje seriale',seriesNotifications:'Powiadomienia',markAllSeriesRead:'Oznacz wszystkie jako przeczytane',moreSeries:'Więcej seriali',moreMySeries:'Więcej moich seriali',moreSeriesNotices:'Więcej powiadomień',orderSeason:'Zamów sezon',orderSeriesEpisode:'Zamów odcinek',followSeries:'Obserwuj',unfollowSeries:'Przestań obserwować',notifySeriesReady:'Powiadamiaj o gotowym sezonie',notifyNewSeriesSeason:'Powiadamiaj o nowych sezonach',autoOrderSeriesSeasons:'Zamawiaj nowe sezony automatycznie',seriesFinale:'Finał',seriesAirsOn:'Emisja {date}',seriesNextSearch:'Następne wyszukiwanie {date}',seriesNoSource:'Nie znaleziono źródła',seriesSourceFailed:'Źródło nie działa',seriesRejected:'Źródło odrzucone',seriesUpcoming:'Jeszcze nie wyemitowano',seriesCollected:'Zebrano, czekamy na sezon',
  seriesSection: "Seriale",
  seriesSeasonReadyNotice: 'Serial „{title}”, sezon {season}, został dodany do biblioteki i jest gotowy do oglądania',
  seriesUnread: "nowe: {count}",
  recentlyAddedSeries: "Ostatnio dodane",
  allSeries: "← Wszystkie seriale",
  seriesEpisodesReady: "Odcinki: {count}",
  minutesShort: "{count} min",
  minutesWatched: "Obejrzano {count} min",
  writers: "Scenariusz",
  seriesLoadFailed: "Nie udało się wczytać seriali",
  seriesOpenFailed: "Nie udało się otworzyć serialu",
  seasonLoadFailed: "Nie udało się wczytać sezonu",
  episodeLoadFailed: "Nie udało się wczytać odcinka",
  episodeNotPlayable: "Ten odcinek nie jest jeszcze dostępny",
  seasonOrderFailed: "Nie udało się zamówić sezonu",
  episodeOrderFailed: "Nie udało się zamówić odcinka",
  followUpdateFailed: "Nie udało się zaktualizować subskrypcji",
  noticeOpenFailed: "Nie udało się otworzyć powiadomienia",
  nextNotAired: "Następny odcinek jeszcze się nie ukazał",
  nextCheckFailed: "Nie udało się sprawdzić następnego odcinka",
  seriesStatusReady: "Gotowy",
  seriesStatusProcessing: "Przygotowywanie",
  seriesStatusDownloading: "Pobieranie",
  seriesStatusQueued: "W kolejce",
  seriesStatusSearching: "Szukamy źródła",
  seriesStatusUnavailable: "Niedostępny",
  seriesStatusNotRequested: "Nie zamówiono",
  seriesEpisodeOrdered: 'Odcinek zamówiony. Pojawi się tutaj, gdy będzie gotowy.', previousEpisode: 'Poprzedni odcinek',
};
const PT: Record<Key, string> = {
  seriesOrderConfirmTitle: 'Adicionar esta temporada à biblioteca?',
  seriesOrderConfirmBody: 'Vamos procurar e preparar a temporada {season} de {title}. Aparecerá aqui quando estiver pronta.',
  seriesWatchedPercent: '{count}% visto',
  episodeProgressFailed: 'Não foi possível guardar o progresso do episódio',
  mySeries:'As minhas séries',seriesNotifications:'Notificações',markAllSeriesRead:'Marcar tudo como lido',moreSeries:'Mais séries',moreMySeries:'Mais das minhas séries',moreSeriesNotices:'Mais notificações',orderSeason:'Pedir temporada',orderSeriesEpisode:'Pedir episódio',followSeries:'Seguir',unfollowSeries:'Deixar de seguir',notifySeriesReady:'Avisar quando uma temporada estiver pronta',notifyNewSeriesSeason:'Avisar sobre novas temporadas',autoOrderSeriesSeasons:'Pedir novas temporadas automaticamente',seriesFinale:'Final',seriesAirsOn:'Estreia em {date}',seriesNextSearch:'Próxima pesquisa {date}',seriesNoSource:'Fonte não encontrada',seriesSourceFailed:'Fonte falhou',seriesRejected:'Fonte rejeitada',seriesUpcoming:'Ainda não estreou',seriesCollected:'Recolhido, à espera da temporada',
  seriesSection: "Séries",
  seriesSeasonReadyNotice: 'A série «{title}», temporada {season}, foi adicionada à biblioteca e está pronta para assistir',
  seriesUnread: "{count} novas",
  recentlyAddedSeries: "Adicionadas recentemente",
  allSeries: "← Todas as séries",
  seriesEpisodesReady: "{count} episódios",
  minutesShort: "{count} min",
  minutesWatched: "{count} min vistos",
  writers: "Argumento",
  seriesLoadFailed: "Não foi possível carregar as séries",
  seriesOpenFailed: "Não foi possível abrir a série",
  seasonLoadFailed: "Não foi possível carregar a temporada",
  episodeLoadFailed: "Não foi possível carregar o episódio",
  episodeNotPlayable: "Este episódio ainda não está disponível",
  seasonOrderFailed: "Não foi possível pedir a temporada",
  episodeOrderFailed: "Não foi possível pedir o episódio",
  followUpdateFailed: "Não foi possível atualizar a subscrição",
  noticeOpenFailed: "Não foi possível abrir a notificação",
  nextNotAired: "O próximo episódio ainda não estreou",
  nextCheckFailed: "Não foi possível verificar o próximo episódio",
  seriesStatusReady: "Pronto",
  seriesStatusProcessing: "A preparar",
  seriesStatusDownloading: "A transferir",
  seriesStatusQueued: "Na fila",
  seriesStatusSearching: "À procura de fonte",
  seriesStatusUnavailable: "Indisponível",
  seriesStatusNotRequested: "Não pedido",
  seriesEpisodeOrdered: 'Episódio pedido. Vai aparecer aqui quando estiver pronto.', previousEpisode: 'Episódio anterior',
};

export const seriesMessages: Record<Locale, Record<Key, string>> = { EN, RU, DE, FR, IT, ES, PL, PT };
